/**
 * Custom error classes for robust error handling
 * 
 * These error classes provide type-safe error handling without relying on
 * fragile string matching in error messages.
 */

/**
 * Base class for application-specific errors
 */
abstract class AppError extends Error {
  abstract readonly errorCode: string;
  
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
    // Maintain proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Errors related to reCAPTCHA operations
 */
export class RecaptchaError extends AppError {
  readonly errorCode = 'RECAPTCHA_ERROR';
  
  constructor(message: string = 'reCAPTCHA verification failed. Please try again.') {
    super(message);
  }
}

/**
 * Errors related to reCAPTCHA timeout specifically
 */
export class RecaptchaTimeoutError extends AppError {
  readonly errorCode = 'RECAPTCHA_TIMEOUT';
  
  constructor(message: string = 'reCAPTCHA verification timed out. Please try again.') {
    super(message);
  }
}

/**
 * Errors related to network timeouts
 */
export class NetworkTimeoutError extends AppError {
  readonly errorCode = 'NETWORK_TIMEOUT';
  
  constructor(message: string = 'Network request timed out. Please check your connection and try again.') {
    super(message);
  }
}

/**
 * Errors related to network connectivity issues
 */
export class NetworkError extends AppError {
  readonly errorCode = 'NETWORK_ERROR';
  
  constructor(message: string = 'Network error occurred. Please check your internet connection.') {
    super(message);
  }
}

/**
 * Errors related to fetch/HTTP request failures
 */
export class FetchError extends AppError {
  readonly errorCode = 'FETCH_ERROR';
  readonly status?: number;
  readonly statusText?: string;
  
  constructor(
    message: string = 'Connection error. Please verify your internet connection.',
    status?: number,
    statusText?: string
  ) {
    super(message);
    this.status = status;
    this.statusText = statusText;
  }
}

/**
 * Errors related to API validation or business logic
 */
export class ValidationError extends AppError {
  readonly errorCode = 'VALIDATION_ERROR';
  readonly field?: string;
  
  constructor(message: string, field?: string) {
    super(message);
    this.field = field;
  }
}

/**
 * Errors related to slow network conditions specifically
 */
export class SlowNetworkError extends AppError {
  readonly errorCode = 'SLOW_NETWORK_ERROR';
  
  constructor(message: string = 'Request failed due to slow network. Please check your connection and try again.') {
    super(message);
  }
}

/**
 * Type guard to check if an error is an application-specific error
 */
export const isAppError = (error: unknown): error is AppError => {
  return error instanceof AppError;
};

/**
 * Type guard to check if an error is a reCAPTCHA-related error
 */
export const isRecaptchaError = (error: unknown): error is RecaptchaError | RecaptchaTimeoutError => {
  return error instanceof RecaptchaError || error instanceof RecaptchaTimeoutError;
};

/**
 * Type guard to check if an error is network-related
 */
export const isNetworkError = (error: unknown): error is NetworkError | FetchError | SlowNetworkError => {
  return error instanceof NetworkError || error instanceof FetchError || error instanceof SlowNetworkError;
};

/**
 * Helper function to create appropriate error based on fetch response
 */
export const createFetchError = async (response: Response): Promise<FetchError> => {
  let message = 'Request failed';
  
  try {
    const errorData = await response.json();
    message = errorData.message || errorData.error || message;
  } catch {
    // If response body is not JSON, use status text
    message = response.statusText || message;
  }
  
  return new FetchError(message, response.status, response.statusText);
};

/**
 * Helper function to wrap fetch with automatic error handling
 */
export const safeFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  try {
    const response = await fetch(input, init);
    
    if (!response.ok) {
      throw await createFetchError(response);
    }
    
    return response;
  } catch (error) {
    // Re-throw our custom errors
    if (isAppError(error)) {
      throw error;
    }
    
    // Handle native fetch errors (network issues, CORS, etc.)
    if (error instanceof TypeError) {
      // TypeError typically indicates network issues in fetch
      throw new NetworkError('Network connection failed. Please check your internet connection.');
    }
    
    // Handle AbortError
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new NetworkTimeoutError('Request was cancelled or timed out.');
    }
    
    // Fallback for unknown errors
    throw new NetworkError(error instanceof Error ? error.message : 'An unknown network error occurred.');
  }
};
